/**
 * Value → needle-angle math for the cockpit panel's 9 `GAUGES.BMP` frames.
 * Pure and unit-tested (no canvas, no DOM) — `CockpitPanel.tsx` only calls
 * these and draws the result.
 *
 * ## Instrument-role mapping
 *
 * Seven of the nine frames are **placed by `RB.EXE`'s own panel table**, which
 * `tools/bake`'s `cockpits` stage reads out of the executable
 * (`packages/baker/src/cockpit-gauges.ts`). Matching that table against the
 * live DOSBox capture of the Camel's cockpit
 * (`docs/game/ui-reference/cockpit-camel-forward.png`) at 1:1 identifies each
 * slot by *position*, not just by reading the dial art — which corrected two
 * of the earlier by-eye guesses:
 *
 * | Frame | Role | Confidence | Evidence |
 * |---|---|---|---|
 * | 0 | Altimeter | CONFIRMED | dial reads "alt", digits 0–9 clockwise; matches the capture at (103,126), 99% of pixels |
 * | 1 | Fuel level (curved sight-tube) | CONFIRMED (position) / INFERRED (role) | the 96×15 ribbon, matched at (114,177) below the panel's centre — exactly where the capture's tick-marked chevron ribbon is |
 * | 2 | Warning lamp | INFERRED, and **not placed** | 8×3 px; it is the one frame `RB.EXE`'s table has no slot for, so the port does not draw it |
 * | 3 | Airspeed indicator | CONFIRMED | dial reads "mph", graduated 40–140; matched at (162,125) |
 * | 4 | Oil temperature | CONFIRMED | dial reads "oil", graduated 40–160; matched at (56,137) |
 * | 5 | **Compass housing** | CONFIRMED (corrected) | matched at (269,154) — the far-right bezel the capture shows reading "NW" with a crosshair, i.e. the compass, not the turn indicator this was previously read as |
 * | 6 | Compass card (rotating tape) | CONFIRMED | literal W/NW/N/NE/E/SE/S/SW letters; scrolls behind frame 5's window |
 * | 7 | Tachometer | CONFIRMED | dial reads "rpm", graduated 0–20 (× 100 rpm); matched at (223,137) |
 * | 8 | **Leftmost dial** (`turnIndicator`) | INFERRED (corrected) | matched at (11,153), the small left-hand dial; its face carries no legible text at this resolution, so the *role* is still a guess — but it is demonstrably not the compass bezel |
 *
 * REALISTIC INSTRUMENTS (manual: "the only instruments seen from the cockpit
 * will be the ones that existed on the original WWI aircraft" vs "all
 * possible instruments"): the era-authentic set is airspeed, altimeter,
 * compass, tachometer and oil; the fuel sight-tube and the unlabelled
 * leftmost dial are the extras, hidden when the toggle is on
 * (`panel-layout.ts`'s `ERA_AUTHENTIC_ROLES`). The split is INFERRED — the
 * manual states the toggle's *behaviour*, not its membership — but the live
 * capture shows all seven at mission start with nothing toggled, which does
 * independently confirm the toggle defaults **off**.
 *
 * **Deviation from the original, deliberate:** the ui-reference lane
 * established that the original locks REALISTIC INSTRUMENTS once a mission
 * has started (`Alt+R` in flight greys it out). This port keeps it live in
 * the settings popover, because our popover is not the Realism Menu and
 * there is no pre-flight screen wired to it yet.
 */

import { clamp, mpsToMph } from "@red-opus/engine";
import { headingDegrees } from "./hud-format.ts";
import type { FlightHud } from "./hud-snapshot.ts";

export type GaugeFrameIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export type InstrumentRole =
  | "altimeter"
  | "fuel"
  | "warningLamp"
  | "airspeed"
  | "oil"
  | "compassBezel"
  | "compassCard"
  | "tachometer"
  | "turnIndicator";

export interface InstrumentRoleEntry {
  readonly frame: GaugeFrameIndex;
  readonly role: InstrumentRole;
  /** Whether visible baked dial text confirms the role, or it's this lane's read of an unlabelled face. */
  readonly confirmed: boolean;
  /** Shown only when REALISTIC INSTRUMENTS is off — not part of the WWI-authentic five. */
  readonly extraInstrument: boolean;
}

function role(frame: GaugeFrameIndex, role: InstrumentRole, confirmed: boolean, extraInstrument: boolean): InstrumentRoleEntry {
  return { frame, role, confirmed, extraInstrument };
}

export const INSTRUMENT_ROLES: readonly InstrumentRoleEntry[] = [
  role(0, "altimeter", true, false),
  role(1, "fuel", false, true),
  role(2, "warningLamp", false, true),
  role(3, "airspeed", true, false),
  role(4, "oil", true, false),
  role(5, "compassBezel", true, false),
  role(6, "compassCard", true, false),
  role(7, "tachometer", true, false),
  role(8, "turnIndicator", false, true),
];

/**
 * Which of the 9 frames count as "extra" (hidden when REALISTIC INSTRUMENTS
 * is on) — the authoritative per-frame table this file's own module doc
 * comment above documents. `panel-layout.ts`'s `ERA_AUTHENTIC_ROLES`/
 * `rolesFor` is the one the panel actually renders from (it works in
 * on-screen *slot* roles, one of which — `"compass"` — folds frames 6 and 8
 * together); `instrument-values.test.ts` pins this function's per-frame
 * answers directly.
 */
export function isExtraInstrument(frameIndex: GaugeFrameIndex): boolean {
  return INSTRUMENT_ROLES.some((entry) => entry.frame === frameIndex && entry.extraInstrument);
}

// Round-dial needle sweeps.

/**
 * The altimeter is a TWO-hand dial — "black-faced round dial, two hands,
 * numerals 0–9 around the full circle" (`docs/game/ui-reference/index.md`,
 * confirmed live against the original). Like a clock: the long hand reads
 * hundreds of feet (one revolution per 1,000 ft), the short hand thousands
 * (one revolution per 10,000 ft). The port drew only the slow hand for a
 * while, which barely moved in ordinary flight and read as broken (user,
 * 2026-08-23). Degrees, clockwise-positive, 0 = up — the convention every
 * function below shares, so `CockpitPanel.tsx` can feed the result straight
 * to `canvas.rotate()`.
 */
export function altimeterHundredsDeg(altitudeFt: number): number {
  const hundreds = (Math.max(0, altitudeFt) / 100) % 10;
  return (hundreds / 10) * 360;
}

export function altimeterNeedleDeg(altitudeFt: number): number {
  const thousands = (Math.max(0, altitudeFt) / 1000) % 10;
  return (thousands / 10) * 360;
}

/**
 * The generic "automotive" gauge sweep shared by airspeed, oil and the
 * tachometer: 270° of travel, minimum value at −135° (7 o'clock) through 0°
 * (12 o'clock) to +135° (5 o'clock) at maximum, with a 90° dead zone at the
 * bottom. `GAUGES.BMP`'s dial art is too small (≤64 px) to recover exact
 * per-tick placement by eye, so this is a deliberate, documented
 * simplification rather than a pixel-measured sweep — chosen because it is
 * the shape a reader already recognises from a real gauge.
 */
export function sweepGaugeAngleDeg(value: number, min: number, max: number): number {
  if (max <= min) return -135;
  const fraction = clamp((value - min) / (max - min), 0, 1);
  return -135 + fraction * 270;
}

/** `docs/game/manual`: no stated airspeed ceiling; 160 mph comfortably clears every flyable type's `vmaxMps` (fastest ≈ 130 mph). */
export const AIRSPEED_SCALE_MPH = { min: 0, max: 160 } as const;
export function airspeedNeedleDeg(mph: number): number {
  return sweepGaugeAngleDeg(mph, AIRSPEED_SCALE_MPH.min, AIRSPEED_SCALE_MPH.max);
}

/** The baked dial's own graduation ("oil 40–160"). */
export const OIL_SCALE = { min: 40, max: 160 } as const;
export function oilNeedleDeg(oilValue: number): number {
  return sweepGaugeAngleDeg(oilValue, OIL_SCALE.min, OIL_SCALE.max);
}

/** The baked dial's own graduation ("rpm 0–20", × 100). */
export const TACHOMETER_SCALE_RPM = { min: 0, max: 2000 } as const;
export function tachometerNeedleDeg(rpm: number): number {
  return sweepGaugeAngleDeg(rpm, TACHOMETER_SCALE_RPM.min, TACHOMETER_SCALE_RPM.max);
}

// Derived (not modelled) readings.

/**
 * `packages/engine` does not model engine RPM (grepped — no `rpm` field
 * anywhere in `SimState`), so the tachometer needle is derived from throttle
 * position rather than a real simulated value: idle ≈ 300 rpm (a running
 * engine does not sit at literal zero) up to the dial's own 2,000 rpm
 * redline at full throttle. SYNTHESIZED — documented here rather than left
 * implicit, per the port-notes appendix.
 */
export const IDLE_RPM = 300;
export function rpmFromThrottle(throttleFraction: number): number {
  const fraction = clamp(throttleFraction, 0, 1);
  return IDLE_RPM + fraction * (TACHOMETER_SCALE_RPM.max - IDLE_RPM);
}

/**
 * Likewise, no oil pressure/temperature model exists — derived from
 * `damage.engine` (1 = undamaged) instead: a healthy engine sits at the
 * dial's own "100" mark (12 o'clock — the design value the baked art marks),
 * rising toward the 160 redline as the engine takes damage (an overheating,
 * failing engine reads hot). SYNTHESIZED.
 */
export const NOMINAL_OIL_VALUE = 100;
export function oilFromEngineHealth(engineHealthFraction: number): number {
  const health = clamp(engineHealthFraction, 0, 1);
  return NOMINAL_OIL_VALUE + (1 - health) * (OIL_SCALE.max - NOMINAL_OIL_VALUE);
}

// Non-round instruments.

/** Bank angle mirrors 1:1 onto the turn indicator's needle, clamped to what the small dial can usefully show. */
export const TURN_INDICATOR_CLAMP_DEG = 60;
export function turnIndicatorNeedleDeg(bankRad: number): number {
  const bankDeg = (bankRad * 180) / Math.PI;
  return clamp(bankDeg, -TURN_INDICATOR_CLAMP_DEG, TURN_INDICATOR_CLAMP_DEG);
}

/** 0 (empty, left edge) .. 1 (full, right edge) along the fuel ribbon's width — a straight clamp, kept as a named function so the mapping is documented and testable rather than inlined at the call site. */
export function fuelPointerFraction(fuelFraction: number): number {
  return clamp(fuelFraction, 0, 1);
}

/**
 * Horizontal source-pixel offset into `GAUGES.BMP` frame 6 (the compass
 * tape) for a given heading. The strip is treated as exactly one 360°
 * loop across its own width — a simplification (the raw art is closer to
 * 1.3 loops end to end) chosen so the same modulo arithmetic works for
 * every heading without special-casing the strip's ends.
 */
export function compassCardOffsetPx(headingDeg: number, stripWidthPx: number): number {
  const wrapped = ((headingDeg % 360) + 360) % 360;
  return (wrapped / 360) * stripWidthPx;
}

/** Frame 2's tiny warning lamp: lit for a jammed gun — the one binary "something needs attention" signal already on the HUD. Port choice; the 8×3 px sprite gives no evidence of its original trigger. */
export function warningLampLit(jammed: boolean): boolean {
  return jammed;
}

// One-call assembly for the panel renderer.

/** Every needle angle/value `CockpitPanel.tsx` needs for one redraw, computed from one `FlightHud` snapshot. */
export interface InstrumentReadings {
  readonly altimeterDeg: number;
  /** The long hand — hundreds of feet, one revolution per 1,000 ft. */
  readonly altimeterHundredsDeg: number;
  readonly airspeedDeg: number;
  readonly oilDeg: number;
  readonly tachometerDeg: number;
  readonly turnIndicatorDeg: number;
  readonly fuelFraction: number;
  readonly headingDeg: number;
  readonly warningLampLit: boolean;
}

export function readingsFromHud(hud: FlightHud): InstrumentReadings {
  return {
    altimeterDeg: altimeterNeedleDeg(hud.raw.altitudeFt),
    altimeterHundredsDeg: altimeterHundredsDeg(hud.raw.altitudeFt),
    airspeedDeg: airspeedNeedleDeg(mpsToMph(hud.raw.airspeedMps)),
    oilDeg: oilNeedleDeg(oilFromEngineHealth(hud.raw.engineHealthFraction)),
    tachometerDeg: tachometerNeedleDeg(rpmFromThrottle(hud.raw.throttleFraction)),
    turnIndicatorDeg: turnIndicatorNeedleDeg(hud.raw.bankRad),
    fuelFraction: fuelPointerFraction(hud.raw.fuelFraction),
    headingDeg: headingDegrees(hud.raw.headingRad),
    warningLampLit: warningLampLit(hud.jammed),
  };
}
